/*
 * @providesModule FriendsTournamentStore
 * 
 */

import _ from 'lodash';
import { _m } from 'INTL';
import FriendsTournamentApiController from 'FriendsTournamentApiController';
import GlobalEventEmitter from 'GlobalEventEmitter';
import FriendInvite from 'FriendInvite';
import Tournament from 'SKZTournament';

const DEFAULT_TOURNAMENT_DATA = {
  "name": "Friends Tournament",
};

class FriendsTournamentStore {
  _user: Object;
  _matchCode: string;
  _share: Object;
  _friends: Array<string>;
  _tournaments: Object;
  _selectedTournament: Object;
  _sectionData: Object;
  _incentiveTournamentsRemaining: number;
  _dataLength: number;

  constructor() {
    this._user = null;
    this._matchCode = '';
    this._share = {
      message: '',
      game_name: '',
      store_url: '',
      icon_url: ''
    };

    this._friends = [];
    this._tournaments = {};
    this._selectedTournament = DEFAULT_TOURNAMENT_DATA;
    this._sectionData = null;
    this._incentiveTournamentsRemaining = null;
    this._dataLength = null;
  }

  getUserInfo(): Object {
    return this._user;
  }

  getFriends(): Array<FriendInvite> {
    return this._friends;
  }

  setFriends(friends: Array<FriendInvite>) {
    this._friends = friends.slice(0);
  }

  getMatchCode(): string {
    return this._matchCode;
  }

  setMatchCode(code: string) {
    this._matchCode = code;
  }

  getSelectedTournament(): Object {
    return this._selectedTournament || DEFAULT_TOURNAMENT_DATA;
  }

  setSelectedTournament(tournament: Tournament) {
    this._selectedTournament = tournament;
  }

  getSelectedTournamentName(): Object {
    const tournament = this._selectedTournament;
    const players = tournament.max_players < 2 ? '' : ' (' + tournament.max_players + ' ' + _m('Players') + ')';
    return this._parseTournamentName(tournament) + players;
  }

  getIncentiveChallengesRemaining(): number {
    return this._incentiveTournamentsRemaining;
  }

  setChallengesAvailable(challenges: number) {
    this._incentiveTournamentsRemaining = challenges;
  }

  isTournamentEmpty(): number {
    return _.size(this._tournaments) == 0;
  }

  getSectionData(): Object {
    return this._sectionData;
  }

  getDataLength(): number {
    return this._dataLength;
  }

  hasNonIncentiveChallenges(): boolean {
    return !!_.find(this._tournaments, (tournament: Tournament): boolean => {return !tournament.incentive;});
  }

  dropIncentiveChallenges() {
    this._tournaments = _.pickBy(this._tournaments, (tournament: Tournament): boolean => {return !tournament.incentive;});
    this._sectionData = this._parseTournaments();
    this._selectedTournament = _.size(this._tournaments) > 0 ? _.values(this._tournaments)[0] : DEFAULT_TOURNAMENT_DATA;

    this._broadcastUpdate();
  }

  setupData(forceUpdate: boolean, selectedTournament: Object = null) {
    if (this.isUpdatePending) {
      return;
    }

    if (!forceUpdate && this._isDataSetupCompleted()) {
      this._broadcastUpdate();
      return;
    }

    this.isUpdatePending = true;
    const completion = (error: unknown) => {
      this.isUpdatePending = false;
      if (!this.isTournamentEmpty()) {
        this._broadcastUpdate(error);
      }
    };

    this._fetchUser(completion);
    this._fetchTournamentData(completion, selectedTournament);
  }

  onTournamentChange(section: string, newTournamentData: Object) {
    this._updateSelectedTournament(section, newTournamentData);
    this._broadcastUpdate();
  }

  getOnSystemTemplates(tournaments: Array<Tournament>): Array<Tournament> {
    return _.filter(tournaments, (tournament: Tournament): boolean => !tournament.isOffSystemFTTemplate());
  }

  _broadcastUpdate(payload: unknown) {
    GlobalEventEmitter.emit('onFriendsTournamentStoreUpdate', payload);
  }

  _isDataSetupCompleted(): boolean {
    return Boolean(this._user && this._selectedTournament && this._matchCode && (this._incentiveTournamentsRemaining || this._incentiveTournamentsRemaining == 0));
  }

  _fetchUser(completion:() => {}) {
    this._user = null;
    FriendsTournamentApiController.fetchUser().then((response: Object) => {
      this._user = response;
      if (this._isDataSetupCompleted()) {
        completion();
      }
    });
  }

  _fetchTournamentData(completion:(error: unknown) => {}, selectedTournament: Object) {
    FriendsTournamentApiController.fetchTournamentData().then((response: Object) => {
      const isValidTournament = selectedTournament &&
        _.get(response, 'tournaments', [])
          .find((tournament: Tournament): boolean => tournament.id == selectedTournament.id);
      this._selectedTournament = isValidTournament ? selectedTournament : null;
      this._handleResponseData(response);
      if (this._isDataSetupCompleted()) {
        completion();
      }
    }).catch((error: unknown) => {
      console.warn('FriendsTournamentStore: fetchTournamentData failed with error: ' + JSON.stringify(error));
      completion(error);
    });
  }

  _fetchIncentiveData(completion: (error: unknown)=>void) {
    this._incentiveTournamentsRemaining = null;

    FriendsTournamentApiController.fetchIncentiveData().then((response: Object) => {
      this._incentiveTournamentsRemaining = response.remaining_friend_challenges;
      if (this._isDataSetupCompleted()) {
        completion();
      }
    }).catch((error: unknown) => {
      completion(error);
    });
  }

  _handleResponseData(response: Object) {
    this._matchCode = _.get(response, 'match_code', '');
    this._share = _.get(response, 'share', '');
    this._tournaments = _.keyBy(_.get(response, 'tournaments', []), 'id');
    this._sectionData = this._parseTournaments();
    this._selectedTournament = this._selectedTournament ? this._selectedTournament : this._getDefaultTournament();
  }

  _parseTournaments(): Array<Object> {
    if (!this._tournaments) {
      return null;
    }

    let length = 0;
    const data = {};
    _.forOwn(this._tournaments, (tournamentData: Object) => {
      const tournament = new Tournament(tournamentData);
      const players = tournament.max_players < 2 ? '' : ' (' + tournament.max_players + ' ' + _m('Players') + ')';
      length++;
      const sectionData = {
        id: tournament.id,
        type: this._parseTournamentName(tournament) + players,
        entry: this._getEntryData(tournament),
        prize: this._getPrizeData(tournament),
      };

      this._addSectionData(sectionData, data);
    });

    this._dataLength = length;
    this._sortSectionData(data);

    return data;
  }

  _parseTournamentName(tournament: Tournament): string {
    if (!tournament) {
      return null;
    }

    switch(tournament.type) {
      case 'UNLIMITED_ENTRY':
        return tournament.category_type == 'LIVE' ? _m('Live Event') : _m('Bracket Tournament');

      case 'LIMITED_ENTRY':
        return _m('Head to Head');

      case 'TURN_BASED':
        return _m('Real Time-Turn Based');

      default:
        return _m('Custom Tournament');
    }
  }

  _getPrizeData(tournament: Tournament): Object {
    return !tournament ? null : {
      prize: tournament.formatted_prize,
      prizeValue: tournament.getTopPrize(),
      sortOrder: this._getSortOrderNumber(tournament.getTopPrizeType()),
      id: tournament.id,
    };
  }

  _getSortOrderNumber(type: string): number {
    switch (type) {
      case 'CASH':
        return 0;
      case 'TICKETZ':
        return 1;
      case 'Z':
        return 2;
      default:
        return -1; // eslint-disable-line no-magic-numbers
    }
  }

  _getEntryData(tournament: Tournament): Object {
    return !tournament ? null : {
      entry: tournament.cash_fee == 0.0 && tournament.z_fee == 0.0 ? _m('FREE') : tournament.formatted_entry_fee,
      id: tournament.id,
    };
  }

  _addSectionData(sectionData: Object, data: Object) {
    if (!sectionData || !data) {
      return;
    }

    let section = data[sectionData.type]; // { type: string, prizes: Array<Object>, entries: Array<Object> }
    if (!section) {
      section = {
        // tournamentData: { prize: string, entry: string, id: number }
        tournamentData: [],
      };
    }

    section.tournamentData.push(_.assign(sectionData.prize, sectionData.entry));
    data[sectionData.type] = section;
  }

  _sortSectionData(sectionData: Object) {
    _.mapValues(sectionData, (data: Object): Object => {
      data.tournamentData = _.orderBy(data.tournamentData, ['sortOrder', 'prizeValue'], ['asc', 'desc']);
      return data;
    });
  }

  _getDefaultTournament(): Tournament {
    if (!_.size(this._sectionData)) {
      return DEFAULT_TOURNAMENT_DATA;
    }

    const defaultTournament = _.values(this._sectionData)[0].tournamentData[0];
    return this._tournaments[defaultTournament.id];
  }

  _updateSelectedTournament(section: string, newTournamentData: Object) {
    if (!section || !newTournamentData) {
      return;
    }

    let id = null;
    let prizeId = null;
    let entryId = null;
    const tournamentData = this._sectionData[newTournamentData.type].tournamentData;

    _.forEach(tournamentData, (data: Object): boolean => {
      if (data.prize == newTournamentData.prize && data.entry == newTournamentData.entry) {
        id = data.id;
        return false;
      } else if (data.prize == newTournamentData.prize) {
        prizeId = data.id;
      } else if (data.entry == newTournamentData.entry) {
        entryId = data.id;
      }
    });

    prizeId = section != 'entry' && prizeId; // De-prioritize prize when an entry is selected
    id = id || prizeId || entryId || tournamentData[0].id;

    this._selectedTournament = this._tournaments[id];
  }
}

export default new FriendsTournamentStore();
